import { supabase } from '@/integrations/supabase/client';
import { isPlaceholderTitle, parseSocialUrl, type SocialLinkInfo } from './socialLinks';
import { decodeHtmlEntities } from './htmlEntities';
import { titleFromPath } from './partnr/sourceDisplay';
import { isImageFirstSocial, socialCardHeight, type CarouselSlide, type SocialMediaType } from './socialCard';

export { parseSocialUrl, isPlaceholderTitle };
export type { SocialLinkInfo };

/** Content of a `link` item, as every add-link path writes it. */
export interface LinkItemContent {
  url: string;
  title: string;
  description: string;
  image: string;
  siteName: string;
  favicon: string;
  imageAspectRatio?: number;
  contentType: string;
  price: string | null;
  screenshot: string | null;
  /** Social links only: 'instagram', 'tiktok', ... */
  platform?: string;
  authorHandle?: string;
  /** Image-first social cards (Instagram, TikTok); see src/lib/socialCard.ts. */
  mediaType?: SocialMediaType;
  authorName?: string;
  /** Re-hosted profile picture. */
  authorAvatar?: string | null;
  authorFollowers?: number;
  /** Profiles: re-hosted covers of the latest posts. */
  recentImages?: string[];
  /** Carousels: re-hosted slides, the first being `image`. */
  slides?: CarouselSlide[];
  previewSource?: string;
  /** No image yet; the card asks once for a deep (Apify) fetch. */
  previewPending?: boolean;
  previewUpdatedAt?: string;
}

/** Long enough for a slow Instagram scrape, short enough that a paste never hangs. */
const PREVIEW_TIMEOUT_MS = 15000;
/** A deep fetch can wait on Apify (up to a minute). It runs in the background. */
const DEEP_PREVIEW_TIMEOUT_MS = 90000;

function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** The card for a URL with nothing fetched: platform, icon and a title from the URL alone. */
export function fallbackLinkContent(url: string): LinkItemContent {
  return mergeLinkMetadata(url, null);
}

/**
 * Combine whatever fetch-link-metadata returned (possibly nothing) with what
 * the URL says on its own. Blocked social pages come back titled "Instagram"
 * or "Log in"; those lose to "Instagram reel by @handle".
 */
export function mergeLinkMetadata(url: string, metadata: Record<string, unknown> | null | undefined): LinkItemContent {
  const m = metadata ?? {};
  const social = parseSocialUrl(url);
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const host = hostname(url);

  const fetchedTitle = decodeHtmlEntities(str(m.title));
  // A page that names nothing better than its domain reads better by its path ("Launch" for producthunt.com/launch)
  const title = social
    ? isPlaceholderTitle(fetchedTitle, social) || fetchedTitle === host ? social.title : fetchedTitle
    : !fetchedTitle || fetchedTitle.toLowerCase().replace(/^www\./, '') === host.toLowerCase()
      ? titleFromPath(url) || fetchedTitle
      : fetchedTitle;
  const fetchedSite = decodeHtmlEntities(str(m.siteName));
  const fetchedType = str(m.contentType);

  return {
    url,
    title,
    description: decodeHtmlEntities(str(m.description)),
    image: str(m.image),
    siteName: social && (!fetchedSite || fetchedSite === host) ? social.platformName : fetchedSite,
    favicon: str(m.favicon) || social?.favicon || '',
    imageAspectRatio: typeof m.imageAspectRatio === 'number' ? m.imageAspectRatio : undefined,
    contentType: social && (!fetchedType || fetchedType === 'website') ? social.contentType : fetchedType || 'website',
    price: str(m.price) || null,
    screenshot: str(m.screenshot) || null,
    ...(social ? { platform: social.platform, authorHandle: str(m.authorHandle) || social.handle } : {}),
    ...(social ? socialFields(m) : {}),
  };
}

const MEDIA_TYPES: SocialMediaType[] = ['image', 'video', 'carousel', 'profile'];

/** The image-first card fields, kept only when the server sent them. */
function socialFields(m: Record<string, unknown>): Partial<LinkItemContent> {
  const out: Partial<LinkItemContent> = {};
  if (typeof m.mediaType === 'string' && MEDIA_TYPES.includes(m.mediaType as SocialMediaType)) out.mediaType = m.mediaType as SocialMediaType;
  if (typeof m.authorName === 'string' && m.authorName) out.authorName = decodeHtmlEntities(m.authorName);
  if (typeof m.authorAvatar === 'string' && m.authorAvatar) out.authorAvatar = m.authorAvatar;
  if (typeof m.authorFollowers === 'number') out.authorFollowers = m.authorFollowers;
  if (Array.isArray(m.recentImages)) out.recentImages = m.recentImages.filter((u): u is string => typeof u === 'string');
  if (Array.isArray(m.slides)) {
    out.slides = m.slides
      .filter((s): s is CarouselSlide => !!s && typeof (s as CarouselSlide).image === 'string')
      .map((s) => (s.video === true ? { image: s.image, video: true } : { image: s.image }));
  }
  if (typeof m.previewSource === 'string') out.previewSource = m.previewSource;
  if (m.previewPending === true) out.previewPending = true;
  if (typeof m.previewUpdatedAt === 'string') out.previewUpdatedAt = m.previewUpdatedAt;
  return out;
}

/**
 * Fetch a link's preview. Never throws and never rejects the link: a failed,
 * blocked or slow fetch still yields a card built from the URL.
 */
export async function fetchLinkPreview(url: string, timeoutMs = PREVIEW_TIMEOUT_MS): Promise<LinkItemContent> {
  const data = await invokeMetadata(url, { deep: false, timeoutMs });
  return data ? mergeLinkMetadata(url, data) : fallbackLinkContent(url);
}

/**
 * Fetch a link's preview again, for "Refresh preview" and old Instagram cards.
 * `deep` also allows the paid Apify fallback (capped per user per day).
 * Returns null when the fetch failed, so the caller keeps what the card has.
 */
export async function fetchFreshLinkPreview(url: string, opts: { deep?: boolean } = {}): Promise<LinkItemContent | null> {
  const deep = opts.deep === true;
  const data = await invokeMetadata(url, { deep, timeoutMs: deep ? DEEP_PREVIEW_TIMEOUT_MS : PREVIEW_TIMEOUT_MS });
  return data ? mergeLinkMetadata(url, data) : null;
}

async function invokeMetadata(
  url: string,
  { deep, timeoutMs }: { deep: boolean; timeoutMs: number },
): Promise<Record<string, unknown> | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), timeoutMs);
    });
    const request = supabase.functions
      .invoke('fetch-link-metadata', { body: deep ? { url, deep } : { url } })
      .then(({ data, error }) => {
        if (error) console.warn('[linkPreview] metadata fetch failed:', url, error);
        return error ? null : (data as Record<string, unknown> | null);
      });
    const data = await Promise.race([request, timeout]);
    if (data === null) console.warn('[linkPreview] no metadata, using the URL only:', url);
    return data;
  } catch (error) {
    console.warn('[linkPreview] metadata fetch threw:', url, error);
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Card height for a link `width` wide (380 on the canvas). Instagram and
 * TikTok cards follow the media's aspect ratio; others are taller when there
 * is an image to show.
 */
export function linkCardHeight(
  content: Pick<LinkItemContent, 'image'> & Partial<LinkItemContent>,
  width = 380,
): number {
  if (isImageFirstSocial(content)) return socialCardHeight(content, width);
  return content.image ? 280 : 120;
}
